<template>
    <Head :title="archetype" />
    <BaseLayout :fill-height="isGeneratingProfile">
        <p class="sr-only" aria-live="polite" aria-atomic="true">{{ isGeneratingProfile ? t("perspective.results.regeneratingAnnouncement") : "" }}</p>
        <LoadingStep v-if="isGeneratingProfile" class="grow" has-perspective :main-loading-message="t('perspective.results.regeneratingMessage')" />
        <div v-else class="max-w-xl mx-auto w-full">
            <BaseButton
                v-if="!hasInProgressSession && !showProfileCtaCard"
                type="button"
                theme="tertiary"
                size="sm"
                class="mb-4 inline-flex items-center gap-2"
                :disabled="isRetaking"
                @click="() => executeRetake()">
                <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
                {{ t("perspective.results.retake") }}
            </BaseButton>
            <!-- In-progress banner. Present from page load and never changes, so it is a note to read
                 in flow rather than a status that would never fire. -->
            <div v-if="hasInProgressSession" role="note" class="mb-10 rounded-lg border border-border bg-surface-sunken px-4 py-3 text-center">
                <p class="body-small text-ink-muted">{{ t("perspective.results.retakeInProgress") }}</p>
                <div class="flex items-center justify-center gap-4 mt-2">
                    <BaseButton type="button" theme="secondary" size="sm" :disabled="isCancelling" @click="router.visit('/perspective/questions/')">
                        {{ t("perspective.results.continueRetake") }}
                    </BaseButton>
                    <BaseButton type="button" theme="tertiary" size="sm" :disabled="isCancelling" @click="isConfirmCancelDialogOpen = true">
                        {{ t("perspective.results.cancelRetake") }}
                    </BaseButton>
                </div>
            </div>

            <!-- Hero -->
            <div class="text-center mb-3">
                <ArchetypeDot :letters="result.letters" class="mx-auto mb-4 size-28 max-md:size-21" />
                <p class="body text-ink-muted mb-1">{{ t("perspective.results.heroLead", { article }) }}</p>
                <h1 class="display-medium text-ink">{{ archetype }}</h1>
            </div>

            <!-- Description -->
            <p v-if="typeData" class="nadia-chat text-ink-muted mb-10">
                {{ typeData.shortDescription }}
            </p>

            <!-- Dimension bars -->
            <div class="space-y-6 mb-10">
                <div v-for="dim in result.dimensions" :key="dim.code">
                    <p class="body-small text-ink-muted mb-2">{{ dimensionTitle(dim.code, dim.binned_score) }}</p>
                    <DimensionBar
                        :code="dim.code"
                        :binned-score="dim.binned_score"
                        :left-label="dimensionLabel(dim.code, 'left')"
                        :right-label="dimensionLabel(dim.code, 'right')" />
                </div>
            </div>

            <!-- Actions -->
            <CollaborationProfileCtaCard
                v-if="showProfileCtaCard"
                :create-href="CREATE_PROFILE_URL"
                :track-create="logProfileCtaClick"
                :chat-href="CHAT_URL"
                :show-retake="!hasInProgressSession"
                :is-retaking="isRetaking"
                @retake="() => executeRetake()" />
            <div v-else class="text-center">
                <div class="inline-flex items-center gap-2">
                    <BaseLink
                        v-if="!collaboration_profile_enabled"
                        href="/accounts/user-profile#tab=archetype"
                        class="base-btn-primary inline-block px-10 py-2.5 body-small-semibold">
                        {{ t("perspective.results.exploreArchetype") }}
                    </BaseLink>
                    <BaseButton v-else-if="has_collaboration_profile" theme="primary" class="px-10 py-2.5 body-small-semibold" @click="handleRegenerateProfile">
                        {{ t("perspective.results.regenerateProfile") }}
                    </BaseButton>
                    <BaseLink v-else :href="CREATE_PROFILE_URL" :before="logProfileCtaClick" class="base-btn-primary inline-block px-10 py-2.5 body-small-semibold">
                        {{ t("perspective.results.generateProfile") }}
                    </BaseLink>
                    <CollaborationProfileTooltip v-if="collaboration_profile_enabled" :regeneration="has_collaboration_profile" />
                </div>
                <p v-if="generationError" role="alert" class="body-small text-chip-status-overdue-text mt-3">{{ generationError }}</p>
                <div class="mt-3">
                    <BaseLink :href="CHAT_URL" class="base-link body-small">{{ t("perspective.results.chatWithNadia") }}</BaseLink>
                </div>
            </div>
        </div>
        <PerspectiveDemoTools v-if="!isGeneratingProfile" />
    </BaseLayout>
    <ConfirmationDialog
        v-model:open="isConfirmCancelDialogOpen"
        :title="t('perspective.results.cancelRetakeConfirm.title')"
        :text="t('perspective.results.cancelRetakeConfirm.text')"
        :confirm-label="t('perspective.results.cancelRetakeConfirm.confirm')"
        :cancel-label="t('perspective.results.cancelRetakeConfirm.keep')"
        @confirm="executeCancelRetake" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
import BaseLink from "~vue/router/BaseLink.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import CollaborationProfileTooltip from "~vue/CollaborationProfile/CollaborationProfileTooltip.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import LoadingStep from "~vue/components/ProfileWizard/LoadingStep.vue";
import { useProfileWizard } from "~vue/composables/useProfileWizard";
import type { Dimension, PerspectiveResult } from "~vue/pages/perspectiveTypes";
import { DIMENSION_LABELS, PERSPECTIVE_TYPES } from "~vue/pages/perspectiveTypes";
import ArchetypeDot from "~vue/Perspective/ArchetypeDot.vue";
import CollaborationProfileCtaCard from "~vue/Perspective/CollaborationProfileCtaCard.vue";
import PerspectiveDemoTools from "~vue/Perspective/PerspectiveDemoTools.vue";
import DimensionBar from "~vue/UserProfile/DimensionBar.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { describeDimensionTendency } from "~vue/utils/perspectiveUtils";
import { articleFor } from "~vue/utils/stringUtils";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

export interface Props {
    result: PerspectiveResult;
    in_progress_session_id: string | null;
    collaboration_profile_enabled: boolean;
    has_collaboration_profile: boolean;
    is_manager: boolean;
    profile_cta_variant: "control" | "treatment" | null;
}

const CREATE_PROFILE_URL = "/profile/create?auto_generate=true";
const CHAT_URL = "/coach?script=perspective&chat_source=perspective_assessment";

const props = defineProps<Props>();
const { t } = useI18n();

const showProfileCtaCard = computed(() => props.profile_cta_variant === "treatment");

async function logProfileCtaEvent(eventName: AmplitudeEventNames): Promise<void> {
    if (props.profile_cta_variant) {
        await logUserInteraction(eventName, { card_type: "perspective_results_profile_cta", page: "perspective_results", variant: props.profile_cta_variant });
    }
}

onMounted(() => {
    void logProfileCtaEvent(AmplitudeEventNames.CARD_VIEWED);
});

function logProfileCtaClick(): Promise<void> {
    return logProfileCtaEvent(AmplitudeEventNames.CARD_CLICKED);
}

const hasInProgressSession = computed(() => props.in_progress_session_id != null);

const isConfirmCancelDialogOpen = ref(false);

const { generateProfile, generationError, isGeneratingProfile } = useProfileWizard({
    isManager: props.is_manager,
    hasPerspectiveReport: true,
});

async function handleRegenerateProfile(): Promise<void> {
    await generateProfile({
        fileIds: [],
        isRecreating: true,
        regenerateFromPerspective: true,
    });
    if (!generationError.value) {
        router.visit("/accounts/collaboration-profile");
    }
}

const { execute: executeRetake, isFetching: isRetaking, onFetchResponse: onRetakeResponse } = useFetch("/v1/perspectives/sessions", { immediate: false }).post({}).json();

onRetakeResponse(() => {
    router.visit("/perspective/questions/");
});

const cancelUrl = computed(() => (props.in_progress_session_id ? `/v1/perspectives/sessions/${props.in_progress_session_id}` : ""));

const { execute: executeCancelRetake, isFetching: isCancelling, onFetchResponse: onCancelResponse } = useFetch(cancelUrl, { immediate: false }).delete();

onCancelResponse(() => {
    router.visit("/perspective/");
});

const typeData = computed(() => PERSPECTIVE_TYPES[props.result.letters] ?? null);
const archetype = computed(() => typeData.value?.title ?? props.result.archetype);
const article = computed(() => articleFor(archetype.value));

function dimensionLabel(code: Dimension, side: "left" | "right"): string {
    return DIMENSION_LABELS[code][side];
}

function dimensionTitle(code: Dimension, binnedScore: number): string {
    return describeDimensionTendency(binnedScore, dimensionLabel(code, "left"), dimensionLabel(code, "right"));
}
</script>
